import React, { useState } from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import withLayout from '../../hocs/Layout';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShieldIcon from '@mui/icons-material/Shield';
import GroupIcon from '@mui/icons-material/Group';
import PageTitle from '../../common/PageTitle';
import PageHero from '../../common/PageHero';
import MUIModal from '../../common/MUIModal';
import Loader from '../../common/Loader';
import KpiCard from './shared/KpiCard';
import PillFilters from './shared/PillFilters';
import FilterBar from './shared/FilterBar';
import { PILL_KEYS, buildPillFilter, getDefaultDateRange, computeTotalMissed } from './shared/constants';
import useAppointmentSearch from './shared/useAppointmentSearch';
import useAppointmentDashboard from './shared/useAppointmentDashboard';
import { buildAppointmentColumns } from './shared/columns';
import ExpandableAppointmentsTable from './shared/ExpandableAppointmentsTable';
import ExportButton from './shared/ExportButton';
import PageBanner from './shared/PageBanner';
import AppointmentDelivery from '../orders/list2/AddAppDelivery';
import MarkMissedModal from './modals/MarkMissedModal';

const PILLS = [
    { key: PILL_KEYS.ALL, label: 'All Missed' },
    { key: PILL_KEYS.MISS_1, label: '1 Miss' },
    { key: PILL_KEYS.MISS_2, label: '2 Misses' },
    { key: PILL_KEYS.MISS_3_PLUS, label: '3+ Misses', danger: true },
    { key: PILL_KEYS.PROZO_FAULT, label: 'Prozo Fault' },
    { key: PILL_KEYS.NON_PROZO, label: 'Non-Prozo' },
    { key: PILL_KEYS.ACTIVE, label: 'Still Active' },
    { key: PILL_KEYS.NEEDS_SCHEDULING, label: 'Needs Scheduling' },
    { key: PILL_KEYS.EVENTUALLY_DELIVERED, label: 'Eventually Delivered' },
];

function MissedRepeat({ abd = true }) {
    const def = getDefaultDateRange();
    const [filters, setFilters] = useState({ fromDate: def.fromDate, toDate: def.toDate });
    const [pill, setPill] = useState(PILL_KEYS.ALL);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(50);
    const [reload, setReload] = useState(0);

    const [scheduleRow, setScheduleRow] = useState(null);
    const [missRow, setMissRow] = useState(null);

    const baseline = [{ field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 }];
    const extraAndFilter = [...baseline, ...buildPillFilter(pill)];

    const { data: searchData, isFetching } = useAppointmentSearch({
        filters, extraAndFilter, page, sizePerPage, reload, appointmentRequired: abd,
    });
    const { data: kpi } = useAppointmentDashboard({
        filters, reload, extraBaseline: baseline, include: ['buckets', 'summary'], appointmentRequired: abd,
    });
    const totalMissed = computeTotalMissed(kpi);

    const counts = {
        [PILL_KEYS.ALL]: totalMissed,
        [PILL_KEYS.MISS_1]: kpi.miss1,
        [PILL_KEYS.MISS_2]: kpi.miss2,
        [PILL_KEYS.MISS_3_PLUS]: kpi.miss3plus,
        [PILL_KEYS.PROZO_FAULT]: kpi.prozoFault,
        [PILL_KEYS.NON_PROZO]: kpi.nonProzo,
        [PILL_KEYS.ACTIVE]: kpi.active,
        [PILL_KEYS.NEEDS_SCHEDULING]: kpi.needsScheduling,
        [PILL_KEYS.EVENTUALLY_DELIVERED]: kpi.eventuallyDelivered,
    };

    const triggerReload = () => setReload((r) => r + 1);

    const columns = buildAppointmentColumns({
        onSchedule: (row) => setScheduleRow(row),
        onMarkMissed: (row) => setMissRow(row),
    });

    return (
        <div id='main-content' className='container-fluid'>
            <PageTitle>Missed &amp; Repeat Deliveries</PageTitle>
            {/* <PageHero title='Missed & Repeat Deliveries' subtitle='Shipments with one or more missed appointments' sx={{ mb: 2.5 }} /> */}
            {isFetching && <Loader />}

            <PageBanner variant='warning' icon={<ReportProblemIcon />} title='What is this page?'>
                Lists all shipments where at least one appointment was missed. Filter by 1, 2, or 3+ missed attempts,
                check whether misses were Prozo&apos;s fault or not, take action on escalations.
            </PageBanner>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='amber' label='Total Missed Shipments' value={totalMissed}
                        sub='Still-pending with 1+ missed appointment' icon={<ReportProblemIcon />} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='red' label='2 Missed Attempts' value={kpi.miss2}
                        sub='Repeated failures' icon={<CancelIcon />} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='red' label='3+ Misses (Escalation)' value={kpi.miss3plus}
                        sub='Needs immediate action' icon={<WarningAmberIcon />} highlightValue borderColor='#c62828' />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='amber' label='Prozo Caused Misses' value={kpi.prozoFault}
                        sub='Prozo attributable' icon={<ShieldIcon />} />
                </Grid>
                <Grid item xs={12} md={2.4}>
                    <KpiCard color='blue' label='Non-Prozo Misses' value={kpi.nonProzo}
                        sub='Brand / consignee caused' icon={<GroupIcon />} />
                </Grid>
            </Grid>

            <PillFilters pills={PILLS} value={pill} onChange={(k) => {
                setPill(k);
                setPage(0);
                if ((k === PILL_KEYS.EVENTUALLY_DELIVERED || k === PILL_KEYS.DELIVERED) && filters.missCategory) {
                    setFilters((f) => ({ ...f, missCategory: '' }));
                }
            }} counts={counts} />

            <FilterBar
                show={['courier', 'attempts', 'missCategory', 'awb', 'dateRange']}
                onApply={(f) => { setFilters(f); setPage(0); triggerReload(); }}
                onReset={() => { setFilters({ fromDate: def.fromDate, toDate: def.toDate }); setPage(0); triggerReload(); }}
            />

            <Stack direction='row' justifyContent='flex-end' sx={{ mb: 1.5 }}>
                <ExportButton
                    label='Export This View'
                    filters={filters}
                    extraAndFilter={extraAndFilter}
                    fileName={`ABD_Missed_${pill}`}
                />
            </Stack>

            <ExpandableAppointmentsTable
                columns={columns}
                rows={searchData?.data?.length > 0 ? searchData.data : []}
                page={page}
                sizePerPage={sizePerPage}
                setPage={setPage}
                setSizePerPage={setSizePerPage}
                rowCount={searchData?.totalRecords ?? -1}
                getRowId={(row) => row.id}
            />

            <MUIModal
                open={!!scheduleRow}
                handleClose={() => setScheduleRow(null)}
                maxWidth='lg'
                hideClose
                title={
                    <Typography variant='subtitle1'>
                        Schedule Appointment — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{scheduleRow?.awb_number}</Typography>
                    </Typography>
                }
            >
                <AppointmentDelivery rowData={scheduleRow} setDelConfirm={() => setScheduleRow(null)} setReload={triggerReload} />
            </MUIModal>

            <MUIModal
                open={!!missRow}
                handleClose={() => setMissRow(null)}
                maxWidth='md'
                title={
                    <Typography variant='subtitle1'>
                        Mark as Missed — AWB:{' '}
                        <Typography component='span' color='error' fontWeight='bold'>{missRow?.awb_number}</Typography>
                    </Typography>
                }
            >
                <MarkMissedModal rowData={missRow} onClose={() => setMissRow(null)} onDone={triggerReload}
                    onScheduleNext={(row) => setScheduleRow(row)} />
            </MUIModal>
        </div>
    );
}

export default withLayout(MissedRepeat);
